import { InformationCircleIcon, PaperClipIcon } from '@heroicons/react/24/solid'
import {
  ChangeEvent,
  FC,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState
} from 'react'
import { v4 } from 'uuid'

import { cn } from '@/app/utils'
import { AppButton } from '@/components/shared/button'
import {
  ButtonSizes,
  ButtonVariants
} from '@/components/shared/button/ui/Button.types'
import { ALL_ALLOWED_EXTENSIONS, isFileAllowedToBeUploaded } from '@/utils/file'

import { UploadedFileType } from '../model/types'
import styles from './FileUploader.module.css'

type Props = {
  disabled?: boolean
  accept?: string[]
  onUpload?: (data: UploadedFileType) => void
  onReadStart?: (data: UploadedFileType) => void
  onReadProgress?: (data: UploadedFileType) => void
  onRead?: (data: UploadedFileType) => void
  multiple?: boolean
  buttonVariant?: ButtonVariants
  buttonSize?: ButtonSizes
  buttonWrapperClassName?: string
  buttonClassName?: string
  buttonText?: string
  buttonIcon?: ReactNode
  isUploading?: boolean
}

const FILE_EXTENSION_ERROR = `Разрешённые расширения файла: ${ALL_ALLOWED_EXTENSIONS.map(
  ext => ext.replace('.', '')
).join(', ')}`

const FileUploader: FC<Props> = ({
  disabled,
  onUpload,
  onRead,
  onReadStart,
  onReadProgress,
  accept = ALL_ALLOWED_EXTENSIONS,
  multiple = true,
  buttonVariant = 'link',
  buttonSize = 'medium',
  buttonWrapperClassName,
  buttonText,
  buttonIcon,
  buttonClassName,
  isUploading
}) => {
  const realInput = useRef<HTMLInputElement | null>(null)
  const htmlId = useId()
  const fileData = useRef<UploadedFileType | null>(null)
  const reader = useRef<FileReader | null>(null)

  const [loading, setLoading] = useState(false)

  const [extensionError, setExtensionError] = useState(false)

  const showText =
    buttonVariant === 'link' || (buttonVariant === 'backless' && buttonText)
  const textToDisplay =
    buttonText ?? (multiple ? 'Прикрепить файлы' : 'Прикрепить файл')

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    setExtensionError(false)
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    const isFileCanBeHandled = await isFileAllowedToBeUploaded(file)

    if (isFileCanBeHandled) {
      handleFile(file)
      return
    }
    setExtensionError(true)
  }

  const handleClick = () => {
    if (realInput.current) {
      realInput.current.click()
    }
  }

  const handleFile = (file: File | undefined) => {
    if (loading || !file) {
      return
    }

    const fileId = v4()
    const data = {
      id: fileId,
      file,
      url: null,
      loading: true,
      progress: 0,
      error: false
    }

    reader.current?.readAsDataURL(file)

    onUpload?.(data)
    fileData.current = data
  }

  const onFileReadStart = (event: ProgressEvent<FileReader>) => {
    if (!fileData.current) {
      return
    }

    const data = {
      ...fileData.current,
      url: (event.target?.result as string) || null
    }

    onReadStart?.(data)
    onRead?.(data)
    fileData.current = data
  }

  const onFileReadProgress = (event: ProgressEvent<FileReader>) => {
    if (!fileData.current) {
      return
    }

    const progress = Math.round((event.loaded / event.total) * 100)
    const data = {
      ...fileData.current,
      url: (event.target?.result as string) || null,
      progress
    }

    onReadProgress?.(data)
    onRead?.(data)
    fileData.current = data
  }

  const onFileRead = async (event: ProgressEvent<FileReader>) => {
    if (!fileData.current) {
      return
    }

    const data = {
      ...fileData.current,
      url: (event.target?.result as string) || null,
      loading: false
    }

    onRead?.(data)
    fileData.current = null
    setLoading(false)

    if (realInput.current) {
      realInput.current.value = ''
    }
  }

  useEffect(() => {
    if (typeof FileReader === 'undefined') {
      return
    }

    const fileReader = new FileReader()
    reader.current = fileReader

    fileReader.addEventListener('load', onFileRead)
    fileReader.addEventListener('loadstart', onFileReadStart)
    fileReader.addEventListener('progress', onFileReadProgress)

    return () => {
      fileReader.removeEventListener('load', onFileRead)
      fileReader.removeEventListener('loadstart', onFileReadStart)
      fileReader.removeEventListener('progress', onFileReadProgress)
      reader.current = null
    }
  }, [])

  return (
    <div>
      {extensionError && (
        <p className="mb-5 flex gap-x-10 font-pro text-red-60 text-16">
          <InformationCircleIcon className="w-35 text-red-60" />{' '}
          {FILE_EXTENSION_ERROR}
        </p>
      )}
      <label
        htmlFor={htmlId}
        className={cn('block max-w-[161px]', buttonWrapperClassName)}
        onClick={handleClick}
      >
        <input
          ref={realInput}
          data-testid="file-uploader-input-id"
          disabled={disabled || loading || isUploading}
          id={htmlId}
          multiple={false}
          accept={accept.join(',')}
          type="file"
          className="peer hidden"
          style={{ display: 'none' }}
          onChange={handleChange}
        />

        <AppButton
          type="button"
          variant={buttonVariant}
          size={buttonSize}
          className={cn(
            'flex items-center gap-x-4 transition peer-disabled:opacity-70',
            styles[buttonVariant],
            buttonClassName
          )}
        >
          {buttonIcon || (
            <PaperClipIcon className={styles[`${buttonSize}Icon`]} />
          )}
          {showText && (
            <span className="font-bold text-16">{textToDisplay}</span>
          )}
        </AppButton>
      </label>
    </div>
  )
}

export default FileUploader
